import {
  RoleDto,
  RolePayload,
  listRoles,
  createRole,
  updateRole,
  deleteRole,
} from "./rolesApi";
import {
  MenuPermissionGridItemDto,
  MenuPermissionItem,
  getPermissionGrid,
  bulkSaveMenuRolePermissions,
} from "./menuRolePermissionsApi";
import { listMenuMasters } from "./menuMastersApi";
import {
  ADMIN_SECTION_MENUS,
  ModuleAccess,
  normalizeMenuName,
} from "./modulePermissions";
import { getAccount } from "./accountsApi";
import {
  UserDto,
  CreateUserPayload,
  UpdateUserPayload,
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
} from "./usersApi";
import {
  BranchDto,
  BranchPayload,
  listBranches,
  createBranch,
  updateBranch,
  deleteBranch,
} from "./branchesApi";
import type { AuditFields } from "./audit";
import {
  AmenityDto,
  AmenityPayload,
  listAmenities,
  createAmenity,
  updateAmenity,
  deleteAmenity,
} from "./amenitiesApi";

export const ORG_STORE_CHANGED_EVENT = "org-store-changed";

export interface Branch extends AuditFields {
  id: string;
  code: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  /** Tax identity printed on tax invoices; blank when not set. */
  gstin: string;
  pan: string;
  /** Supplier GST profile for e-invoicing; blank when not set. */
  legalName: string;
  tradeName: string;
  addressLine2: string;
  gstRegistrationType: string;
  eInvoiceEnabled: boolean;
  /** yyyy-MM-dd, or "" when not set. */
  eInvoiceApplicableFrom: string;
  /** ISO 4217, e.g. "INR". */
  currencyCode: string;
  /** IANA id, e.g. "Asia/Kolkata". */
  timeZone: string;
  isHeadOffice: boolean;
  status: "active" | "inactive";
}

export interface Amenity extends AuditFields {
  id: string;
  name: string;
  description: string;
  /** Branches the amenity is offered at; empty = shared by every branch. */
  branchIds: string[];
  type: string;
  status: "active" | "inactive";
}

export const PERMISSION_MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "bookings", label: "Bookings" },
  { key: "events", label: "Events & Halls" },
  { key: "quotations", label: "Quotations" },
  { key: "roomService", label: "Room Service" },
  { key: "restaurant", label: "Restaurant" },
  { key: "rooms", label: "Rooms" },
  { key: "packages", label: "Packages" },
  { key: "eventTypes", label: "Event Types" },
  { key: "housekeeping", label: "Housekeeping" },
  { key: "settings", label: "Settings" },
  { key: "usersRoles", label: "Users & Roles" },
] as const;

export type ModuleKey = (typeof PERMISSION_MODULES)[number]["key"];

/** One row of a role's permission matrix — backed by the real
 * MenuMasters/MenuRolePermissions API (see below), keyed by the backend's
 * numeric MenuId rather than the local ModuleKey list Plans still use. */
export interface MenuPermissionRow {
  menuId: number;
  menuDisplayName: string;
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
  print: boolean;
  export: boolean;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: MenuPermissionRow[];
}

export interface StaffUser extends AuditFields {
  id: string;
  name: string;
  email: string;
  phone: string;
  roleId: string;
  branchId: string;
  status: "active" | "inactive";
}

// ---- Branches — backed by the live Branches API. ----

const toBranch = (dto: BranchDto): Branch => ({
  id: String(dto.branchId),
  code: dto.branchCode ?? "",
  name: dto.branchName ?? "",
  contactPerson: dto.contactPerson ?? "",
  email: dto.email ?? "",
  phone: dto.phone ?? "",
  address: dto.address ?? "",
  city: dto.city ?? "",
  state: dto.state ?? "",
  country: dto.country ?? "",
  zipCode: dto.zipCode ?? "",
  gstin: dto.gstin ?? "",
  pan: dto.pan ?? "",
  legalName: dto.legalName ?? "",
  tradeName: dto.tradeName ?? "",
  addressLine2: dto.addressLine2 ?? "",
  gstRegistrationType: dto.gstRegistrationType ?? "",
  eInvoiceEnabled: !!dto.eInvoiceEnabled,
  eInvoiceApplicableFrom: (dto.eInvoiceApplicableFrom ?? "").slice(0, 10),
  currencyCode: dto.currencyCode ?? "",
  timeZone: dto.timeZone ?? "",
  isHeadOffice: dto.isHeadOffice,
  status: dto.isActive ? "active" : "inactive",
  createdBy: dto.createdBy,
  createdByName: dto.createdByName,
  createdOn: dto.createdOn,
  modifiedBy: dto.modifiedBy,
  modifiedByName: dto.modifiedByName,
  modifiedOn: dto.modifiedOn,
});

export interface BranchInput {
  code: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  /** Tax identity printed on tax invoices; blank when not set. */
  gstin: string;
  pan: string;
  /** Supplier GST profile for e-invoicing; "" clears a field on save. */
  legalName: string;
  tradeName: string;
  addressLine2: string;
  gstRegistrationType: string;
  eInvoiceEnabled: boolean;
  /** yyyy-MM-dd, or "" when not set. */
  eInvoiceApplicableFrom: string;
  /** ISO 4217, e.g. "INR". */
  currencyCode: string;
  /** IANA id, e.g. "Asia/Kolkata". */
  timeZone: string;
  isHeadOffice: boolean;
  status: Branch["status"];
}

const toBranchPayload = (
  accountId: number,
  input: BranchInput
): BranchPayload => ({
  accountId,
  branchCode: input.code,
  branchName: input.name,
  contactPerson: input.contactPerson,
  email: input.email,
  phone: input.phone,
  address: input.address,
  city: input.city,
  state: input.state,
  country: input.country,
  zipCode: input.zipCode,
  gstin: input.gstin || null,
  pan: input.pan || null,
  // Sent as typed: "" clears a stored value (null would keep it).
  legalName: input.legalName,
  tradeName: input.tradeName,
  addressLine2: input.addressLine2,
  gstRegistrationType: input.gstRegistrationType,
  eInvoiceEnabled: input.eInvoiceEnabled,
  eInvoiceApplicableFrom: input.eInvoiceApplicableFrom || null,
  currencyCode: input.currencyCode || null,
  timeZone: input.timeZone || null,
  isHeadOffice: input.isHeadOffice,
  isActive: input.status === "active",
});

/** Unlike Roles/Users, the live Branches endpoint filters server-side on
 * accountId — there's no "fetch all" mode. */
export const fetchBranches = async (accountId: number): Promise<Branch[]> => {
  const all = await listBranches(accountId);
  return all.map(toBranch);
};

/** Branch codes are system generated — the Add Branch form no longer asks for
 * one, and the backend wants a short unique code per branch. Same shape as
 * tenantStore's account/plan codes. */
const generateBranchCode = (name: string): string => {
  const base = slugify(name).toUpperCase().slice(0, 12) || "BRANCH";
  const suffix = Date.now().toString(36).toUpperCase().slice(-5);
  return `${base}-${suffix}`;
};

export const createBranchForAccount = async (
  accountId: number,
  input: BranchInput
): Promise<Branch> => {
  const code = input.code.trim() || generateBranchCode(input.name);
  const dto = await createBranch(toBranchPayload(accountId, { ...input, code }));
  return toBranch(dto);
};

export const updateBranchById = async (
  id: string,
  accountId: number,
  input: BranchInput
): Promise<Branch> => {
  await updateBranch(Number(id), toBranchPayload(accountId, input));
  return toBranch({
    branchId: Number(id),
    accountId,
    branchCode: input.code,
    branchName: input.name,
    contactPerson: input.contactPerson,
    email: input.email,
    phone: input.phone,
    address: input.address,
    city: input.city,
    state: input.state,
    country: input.country,
    zipCode: input.zipCode,
    gstin: input.gstin || null,
    pan: input.pan || null,
    legalName: input.legalName || null,
    tradeName: input.tradeName || null,
    addressLine2: input.addressLine2 || null,
    gstRegistrationType: input.gstRegistrationType || null,
    eInvoiceEnabled: input.eInvoiceEnabled,
    eInvoiceApplicableFrom: input.eInvoiceApplicableFrom || null,
    currencyCode: input.currencyCode || null,
    timeZone: input.timeZone || null,
    logoUrl: null,
    statusName: null,
    isHeadOffice: input.isHeadOffice,
    isActive: input.status === "active",
  });
};

export const deleteBranchById = async (id: string): Promise<void> => {
  await deleteBranch(Number(id));
};

// ---- Amenities — backed by the live Amenities API. ----

const toAmenity = (dto: AmenityDto): Amenity => ({
  id: String(dto.amenityId),
  name: dto.amenityName ?? "",
  description: dto.description ?? "",
  // Older API builds only send the single branchId.
  branchIds: (dto.branchIds ?? (dto.branchId === null ? [] : [dto.branchId])).map(
    String
  ),
  type: dto.amenityType ?? "",
  status: dto.isActive ? "active" : "inactive",
  createdBy: dto.createdBy,
  createdByName: dto.createdByName,
  createdOn: dto.createdOn,
  modifiedBy: dto.modifiedBy,
  modifiedByName: dto.modifiedByName,
  modifiedOn: dto.modifiedOn,
});

export interface AmenityInput {
  name: string;
  description: string;
  /** Empty = shared by every branch. */
  branchIds: string[];
  type: string;
  status: Amenity["status"];
}

const toAmenityPayload = (
  accountId: number,
  input: AmenityInput
): AmenityPayload => ({
  accountId,
  branchId: input.branchIds.length === 1 ? Number(input.branchIds[0]) : null,
  branchIds: input.branchIds.map(Number),
  amenityName: input.name,
  description: input.description,
  amenityType: input.type.trim() === "" ? null : input.type.trim(),
  isActive: input.status === "active",
});

/** Pass `accountId` to scope to one tenant's amenities; omit to fetch all
 * (the live endpoint's accountId/branchId filters are optional, so filtering
 * happens client-side, same as Roles/Users). */
export const fetchAmenities = async (accountId?: number): Promise<Amenity[]> => {
  const all = await listAmenities();
  const scoped =
    accountId === undefined ? all : all.filter((a) => a.accountId === accountId);
  return scoped.map(toAmenity);
};

export const createAmenityForAccount = async (
  accountId: number,
  input: AmenityInput
): Promise<Amenity> => {
  const dto = await createAmenity(toAmenityPayload(accountId, input));
  return toAmenity(dto);
};

export const updateAmenityById = async (
  id: string,
  accountId: number,
  input: AmenityInput
): Promise<Amenity> => {
  await updateAmenity(Number(id), toAmenityPayload(accountId, input));
  return toAmenity({
    amenityId: Number(id),
    accountId,
    branchId: input.branchIds.length === 1 ? Number(input.branchIds[0]) : null,
    branchIds: input.branchIds.map(Number),
    amenityName: input.name,
    description: input.description,
    amenityType: input.type.trim() === "" ? null : input.type.trim(),
    isActive: input.status === "active",
  });
};

export const deleteAmenityById = async (id: string): Promise<void> => {
  await deleteAmenity(Number(id));
};

// ---- Roles — backed by the live Roles API. Per-module permissions are now
// backed by the live MenuMasters/MenuRolePermissions API too (previously
// kept in localStorage, keyed by roleId, before those endpoints existed). ----

const gridItemToRow = (item: MenuPermissionGridItemDto): MenuPermissionRow => ({
  menuId: item.menuId,
  menuDisplayName: item.menuDisplayName,
  view: item.canView,
  add: item.canAdd,
  edit: item.canEdit,
  delete: item.canDelete,
  print: item.canPrint,
  export: item.canExport,
});

const rowToPermissionItem = (row: MenuPermissionRow): MenuPermissionItem => ({
  menuId: row.menuId,
  canView: row.view,
  canAdd: row.add,
  canEdit: row.edit,
  canDelete: row.delete,
  canPrint: row.print,
  canExport: row.export,
});

/** One row per active menu module, all flags false — what a brand-new role
 * (no roleId yet, so the grid endpoint can't be queried) starts from. */
export const emptyMenuPermissions = async (): Promise<MenuPermissionRow[]> => {
  const menus = await listMenuMasters();
  return menus
    .filter((m) => m.isActive)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((m) => ({
      menuId: m.menuId,
      menuDisplayName: m.displayName,
      view: false,
      add: false,
      edit: false,
      delete: false,
      print: false,
      export: false,
    }));
};

/** What the given (accountId, roleId) may do in each module, keyed by the
 * normalized `MenuMaster.menuName` — for gating the Sidebar, routes and API
 * writes (see PermissionsContext). Joins the grid (keyed by numeric MenuId)
 * against MenuMasters (which carries the stable `menuName` string) since the
 * grid endpoint itself only returns `menuDisplayName`, not `menuName`.
 * Modules the role can't view are left out entirely. */
export const fetchMenuAccess = async (
  accountId: number,
  roleId: number,
  userId: number
): Promise<Map<string, ModuleAccess>> => {
  const [menus, grid, account] = await Promise.all([
    listMenuMasters(),
    getPermissionGrid(accountId, roleId),
    // A failed read just means "not the Primary Admin" — the API refuses
    // their writes either way.
    getAccount(accountId).catch(() => null),
  ]);
  const isPrimaryAdmin =
    !!account?.primaryAdminUserId && account.primaryAdminUserId === userId;
  const nameByMenuId = new Map(menus.map((m) => [m.menuId, m.menuName]));
  const access = new Map<string, ModuleAccess>();
  grid.forEach((row) => {
    if (!row.canView) return;
    const name = nameByMenuId.get(row.menuId);
    if (!name) return;
    const key = normalizeMenuName(name);
    // Admin sections are view-only for everyone but the Primary Admin.
    const adminLocked = ADMIN_SECTION_MENUS.includes(key) && !isPrimaryAdmin;
    access.set(key, {
      view: true,
      // Add isn't offered on the Roles screen, so creating counts as Edit.
      edit: !adminLocked && (row.canEdit || row.canAdd),
      delete: !adminLocked && row.canDelete,
    });
  });
  return access;
};

const toRole = (dto: RoleDto, permissions: MenuPermissionRow[]): Role => ({
  id: String(dto.roleId),
  name: dto.roleName,
  description: dto.description,
  permissions,
});

export interface RoleInput {
  name: string;
  description: string;
  permissions: MenuPermissionRow[];
}

/** Pass `accountId` to scope to one tenant's roles; omit to fetch all (the
 * live Roles endpoint doesn't take a filter query param, so filtering
 * happens client-side). Each role's permission grid is fetched by its own
 * accountId (from the role row itself), not the filter param. */
export const fetchRoles = async (accountId?: number): Promise<Role[]> => {
  const all = await listRoles();
  const scoped =
    accountId === undefined ? all : all.filter((r) => r.accountId === accountId);
  return Promise.all(
    scoped.map(async (dto) => {
      const grid = await getPermissionGrid(dto.accountId, dto.roleId);
      return toRole(dto, grid.map(gridItemToRow));
    })
  );
};

export const createRoleForAccount = async (
  accountId: number,
  input: RoleInput
): Promise<Role> => {
  const payload: RolePayload = {
    accountId,
    roleName: input.name,
    description: input.description,
    isActive: true,
  };
  const dto = await createRole(payload);
  await bulkSaveMenuRolePermissions(
    accountId,
    dto.roleId,
    input.permissions.map(rowToPermissionItem)
  );
  return toRole(dto, input.permissions);
};

export const updateRoleById = async (
  id: string,
  accountId: number,
  input: RoleInput
): Promise<Role> => {
  const payload: RolePayload = {
    accountId,
    roleName: input.name,
    description: input.description,
    isActive: true,
  };
  await updateRole(Number(id), payload);
  await bulkSaveMenuRolePermissions(
    accountId,
    Number(id),
    input.permissions.map(rowToPermissionItem)
  );
  return toRole(
    {
      roleId: Number(id),
      accountId,
      roleName: input.name,
      description: input.description,
      isActive: true,
    },
    input.permissions
  );
};

export const deleteRoleById = async (id: string): Promise<void> => {
  await deleteRole(Number(id));
};

// ---- Users — backed by the live Users API. ----

const toStaffUser = (dto: UserDto): StaffUser => ({
  id: String(dto.userId),
  name: dto.userName,
  email: dto.email,
  phone: dto.mobileNo,
  roleId: String(dto.roleId),
  branchId: dto.branchId === null ? "" : String(dto.branchId),
  status: dto.isActive ? "active" : "inactive",
  createdBy: dto.createdBy,
  createdByName: dto.createdByName,
  createdOn: dto.createdOn,
  modifiedBy: dto.modifiedBy,
  modifiedByName: dto.modifiedByName,
  modifiedOn: dto.modifiedOn,
});

export interface UserInput {
  name: string;
  email: string;
  phone: string;
  roleId: string;
  branchId: string;
  status: StaffUser["status"];
}

/** Pass `accountId` to scope to one tenant's users; omit to fetch all (the
 * live endpoint doesn't take a filter query param, so filtering happens
 * client-side). */
export const fetchUsers = async (accountId?: number): Promise<StaffUser[]> => {
  const all = await listUsers();
  const scoped =
    accountId === undefined ? all : all.filter((u) => u.accountId === accountId);
  return scoped.map(toStaffUser);
};

export const createUserForAccount = async (
  accountId: number,
  input: UserInput & { password: string }
): Promise<StaffUser> => {
  const payload: CreateUserPayload = {
    accountId,
    branchId: input.branchId === "" ? null : Number(input.branchId),
    userName: input.name,
    email: input.email,
    password: input.password,
    mobileNo: input.phone,
    roleId: Number(input.roleId) || 0,
    isActive: input.status === "active",
  };
  const dto = await createUser(accountId, payload);
  return toStaffUser(dto);
};

export const updateUserById = async (
  id: string,
  accountId: number,
  input: UserInput
): Promise<StaffUser> => {
  const payload: UpdateUserPayload = {
    accountId,
    branchId: input.branchId === "" ? null : Number(input.branchId),
    userName: input.name,
    email: input.email,
    mobileNo: input.phone,
    roleId: Number(input.roleId) || 0,
    isActive: input.status === "active",
  };
  await updateUser(Number(id), payload);
  return toStaffUser({
    userId: Number(id),
    accountId,
    branchId: payload.branchId,
    userName: input.name,
    email: input.email,
    mobileNo: input.phone,
    roleId: Number(input.roleId) || 0,
    roleName: "",
    isActive: input.status === "active",
  });
};

export const resetUserPasswordById = async (
  id: string,
  newPassword: string
): Promise<void> => {
  await resetUserPassword(Number(id), newPassword);
};

export const deleteUserById = async (id: string): Promise<void> => {
  await deleteUser(Number(id));
};

export const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
